import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  OutlinedInput,
  FormControl,
  InputLabel,
  FormHelperText,
  keyframes,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../src/API/Auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../src/Context/AuthContext";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { tokens } from "../theme/tokens";

const Login = () => {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    username: "",
    password: "",
  });

  const validateEmail = (email) => {
    const value = String(email || "").trim().toLowerCase();
    if (!value) return false;

    // Basic email structure + safe local/domain characters.
    const basicPattern =
      /^(?!.*\.\.)[a-z0-9._%+-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
    if (!basicPattern.test(value)) return false;

    // Allow only these endings:
    // .com, .in, .edu, .tech, .co.uk, .co.in
    const allowedEndings = [".com", ".in", ".edu", ".tech", ".co.uk", ".co.in"];
    return allowedEndings.some((suffix) => value.endsWith(suffix));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };


  const handleLogin = async (e) => {
  if (e?.preventDefault) e.preventDefault();

  if (!formData.username.trim()) {
    setErrors((prev) => ({
      ...prev,
      username: "Email is require",
    }));
    return;
  }

  if (!validateEmail(formData.username)) {
    setErrors((prev) => ({
      ...prev,
      username: "Please enter a valid email",
    }));
    return;
  }

  if (!formData.password) {
    setErrors((prev) => ({
      ...prev,
      password: "Password is required",
    }));
    return;
  }

  setIsLoading(true);

  try {
    const response = await authAPI.login({
      username: formData.username,
      password: formData.password,
    });

    if (response.success) {
      toast.success("Login successful!");

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userName", response.user.userName);
      localStorage.setItem("userId", String(response.user.id));
      if (response.user.clientId != null) {
        localStorage.setItem("clientId", String(response.user.clientId));
      }
      if (response.user.role != null) {
        localStorage.setItem("userRole", String(response.user.role));
      }
      if (response.user.isMasterUser != null) {
        localStorage.setItem("isMasterUser", String(response.user.isMasterUser));
      }

      const accessToken = response.token;
      const refreshToken = response.refreshToken;

      await login(
        accessToken,
        refreshToken,
        response.tokenExpiration,
        response.refreshTokenExpiration
      );

      setTimeout(() => {
        navigate("/", { replace: true, state: { reload: true } });
      }, 1000);

    } else {
      toast.error(response.message || "Login failed");
    }
  } catch (error) {
    toast.error("Login failed. Please try again.");
  } finally {
    setIsLoading(false);
  }
};

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLogin(event);
    }
  };

  const backgroundImages = [
    "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80", // Luxury hotel lobby
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80", // Modern hotel room
    "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80", // Luxury pool
    "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80", // Hotel exterior
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

  return (
    <Box
    sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative", // Add this
      overflow: "hidden",   // Add this
    }}
    >
      <ToastContainer />
      {backgroundImages.map((image, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: currentImageIndex === index ? 1 : 0,
            transition: "opacity 1s ease-in-out",
            animation:
              currentImageIndex === index ? `${fadeIn} 1s ease-in-out` : "none",
            zIndex: -1,
          }}
        />
      ))}
      <Container maxWidth="sm"   sx={{ 
          position: "relative",  // Add this
          zIndex: 1,            // Add this
        }}>
        <Paper
          elevation={3}
          // sx={{ p: 4, textAlign: "center" }}>
          sx={{
            p: { xs: 2, sm: 4 },
            // marginRight:{xs:30},
            width: "100%",
            maxWidth: { xs: "90%", sm: 380 },
            // minHeight: 350,
            minHeight: { xs: "auto", sm: 350 },
            textAlign: "center",
            borderRadius: { xs: "16px", sm: "20px" },
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            mx: "auto",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <img src="/images/Wordmark.svg" alt="Hotel Logo" height={30} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontFamily: tokens.fontDisplay,
              fontWeight: 700,
              color: tokens.brand,
              letterSpacing: "-0.02em",
            }}
          >
            Sign in
          </Typography>

               <Box component="div" sx={{ mt: 2 }}>
                   <FormControl fullWidth error={!!errors.username} sx={{ mb: 3 }}>
                     <InputLabel>Email</InputLabel>
                     <OutlinedInput
                       label="Email"
                       name="username"
                       type="email"
                       value={formData.username}
                       onChange={handleChange}
                       onKeyDown={handleKeyDown}
                     />
                     {errors.username && (
                       <FormHelperText>{errors.username}</FormHelperText>
                     )}
                   </FormControl>
       
                   <TextField
                     fullWidth
                     label="Password"
                     type={showPassword ? "text" : "password"}
                     name="password"
                     value={formData.password}
                     onChange={handleChange}
                     onKeyDown={handleKeyDown}
                     error={!!errors.password}
                     helperText={errors.password}
                     sx={{ mb: 1 }}
                     InputProps={{
                       endAdornment: (
                         <InputAdornment position="end">
                           <IconButton
                             onClick={() => setShowPassword(!showPassword)}
                             edge="end"
                             type="button"
                           >
                             {showPassword ? <VisibilityOff /> : <Visibility />}
                           </IconButton>
                         </InputAdornment>
                       ),
                     }}
                   />
                   
                   <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                     <Button
                       onClick={() => navigate("/forgot-password")}
                       type="button"
                       sx={{
                         textTransform: 'none',
                         color: tokens.brand,
                         '&:hover': {
                           backgroundColor: 'transparent',
                           textDecoration: 'underline'
                         }
                       }}
                     >
                       Forgot Password?
                     </Button> 
                   </Box>
       
                   <Button
                     fullWidth
                     variant="contained"
                     type="button"
                     onClick={handleLogin}
                     disabled={isLoading}
                     sx={{ mb: 2, borderRadius: `${tokens.radiusMd}px`, p: 1.5, fontWeight: 700 }}
                   >
                     {isLoading ? <CircularProgress size={24} /> : "Login"}
                   </Button>
       
                   {/* <Box sx={{ mt: 1, pt: 2, borderTop: 1, borderColor: "divider" }}>
                     <Typography variant="body2" color="text.secondary">
                       Don't have an account?{" "}
                       <Button
                         onClick={() => navigate("/signup")}
                         sx={{
                           padding: { xs: "10px", sm: "12px" },
                           fontSize: { xs: "14px", sm: "16px" },
                           fontWeight: 500,
                           borderRadius: { xs: "25px", sm: "30px" },
                           color: "#1B3C73",
                         }}
                       >
                         Sign up
                       </Button>
                     </Typography>
                   </Box> */}
                 </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
